import { useState } from "react";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

const AI_SUPPORT_MODE_OPTIONS = [
  {
    value: "storedPictures",
    label: "オーナー名検索",
    description: "所有者名や所在地から取得済み登記を検索",
    Icon: ManageSearchOutlinedIcon,
  },
  {
    value: "manual",
    label: "マニュアル検索",
    description: "R.E.DATA Plusの操作方法を検索",
    Icon: MenuBookOutlinedIcon,
  },
] as const;

export type AiSupportMode = (typeof AI_SUPPORT_MODE_OPTIONS)[number]["value"];

interface AiSupportModeSelectorProps {
  value: AiSupportMode;
  disabled: boolean;
  onChange: (value: AiSupportMode) => void;
}

export const AiSupportModeSelector: React.FC<AiSupportModeSelectorProps> = ({
  value,
  disabled,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const selectedMode = AI_SUPPORT_MODE_OPTIONS.find(
    (option) => option.value === value
  );

  if (selectedMode === undefined) {
    return null;
  }

  const SelectedModeIcon = selectedMode.Icon;

  return (
    <>
      <Button
        id="ai-support-mode-selector"
        size="small"
        color="inherit"
        disabled={disabled}
        aria-label={`モード: ${selectedMode.label}`}
        aria-controls={isOpen ? "ai-support-mode-menu" : undefined}
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : undefined}
        startIcon={<SelectedModeIcon fontSize="small" />}
        endIcon={
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 150ms ease",
            }}
          />
        }
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          minWidth: 0,
          minHeight: 32,
          px: 1,
          borderRadius: 1.5,
          color: "text.secondary",
          lineHeight: 1,
          textTransform: "none",
        }}
      >
        {selectedMode.label}
      </Button>
      <Menu
        id="ai-support-mode-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null);
        }}
        MenuListProps={{
          "aria-label": "AIサポートのモード",
          sx: { p: 0.75 },
        }}
        PaperProps={{
          sx: {
            width: 344,
            maxWidth: "calc(100vw - 32px)",
          },
        }}
      >
        {AI_SUPPORT_MODE_OPTIONS.map((option) => {
          const isSelected = option.value === value;
          const ModeIcon = option.Icon;

          return (
            <MenuItem
              key={option.value}
              disabled={disabled}
              role="menuitemradio"
              aria-checked={isSelected}
              selected={isSelected}
              onClick={() => {
                setAnchorEl(null);
                if (!isSelected) {
                  onChange(option.value);
                }
              }}
              sx={{
                alignItems: "center",
                borderRadius: 1,
                whiteSpace: "normal",
              }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                <ModeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                sx={{ my: 0 }}
                primary={option.label}
                secondary={option.description}
                primaryTypographyProps={{ variant: "body2", fontWeight: 700 }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
              {isSelected && (
                <CheckRoundedIcon
                  color="primary"
                  fontSize="small"
                  sx={{ ml: 1, flexShrink: 0 }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
